import { Link } from 'react-router-dom';
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, contactPhoneHref } from '../../marketing/contact';

const EFFECTIVE_DATE = 'September 20, 2026';

/**
 * Public, unauthenticated privacy policy page (required for Meta App Review's
 * "Privacy Policy URL" field, and for Facebook Login / WhatsApp Business
 * Platform usage generally). Deliberately outside both `ProtectedRoute` and
 * `PublicOnlyRoute` in `app/router.tsx` - anyone, logged in or not (including
 * Meta's reviewers), must be able to load this page directly.
 *
 * Content review note: the placeholder contact email/address below should be
 * confirmed or replaced with the company's real values, and this page should
 * be reviewed by whoever handles legal/compliance for Neuro Edge Technologies
 * LLP before relying on it as the company's actual privacy policy.
 */
export function PrivacyPolicyPage() {
  return (
    <div
      style={{
        maxWidth: 800,
        margin: '0 auto',
        padding: 'var(--space-6) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-5)',
        lineHeight: 1.6,
      }}
    >
      <div>
        <h1 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-xl)' }}>
          Privacy Policy
        </h1>
        <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Effective date: {EFFECTIVE_DATE}
        </p>
      </div>

      <section>
        <p>
          NeuroEdge Connect (&ldquo;NeuroEdge Connect&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) is
          operated by <strong>Neuro Edge Technologies LLP</strong>. NeuroEdge Connect is a customer
          engagement and automation platform that lets a business (a &ldquo;client&rdquo; or
          &ldquo;tenant&rdquo;) manage contacts, leads, and conversations with its own customers,
          including over WhatsApp. This policy explains what information we collect, how we use it,
          and the choices available to you, whether you are a client business using NeuroEdge
          Connect or a customer messaging one of our clients on WhatsApp.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>1. Information We Collect</h2>
        <p>
          <strong>Account and business information.</strong> When a business signs up, we collect
          the business name, the account owner&rsquo;s name and email address, and a securely hashed
          password (we never store passwords in plain text).
        </p>
        <p>
          <strong>Customer relationship data.</strong> A client business may enter or import
          information about its own contacts and leads into NeuroEdge Connect &mdash; for example
          names, phone numbers, email addresses, notes, tasks, and pipeline/deal information. This
          data belongs to, and is controlled by, the client business that entered it.
        </p>
        <p>
          <strong>WhatsApp messages and metadata.</strong> When a client connects its own WhatsApp
          Business Account, we process the messages exchanged between that business and its
          customers &mdash; message content, timestamps, delivery/read status, and attachment
          metadata &mdash; so the business can view and reply to conversations, and so we can power
          optional automated replies the business configures.
        </p>
        <p>
          <strong>Facebook / Meta Platform Data.</strong> To connect a WhatsApp Business Account, a
          client business authorizes NeuroEdge Connect through Meta&rsquo;s Embedded Signup
          (Facebook Login for Business). Through this authorization we receive: the public profile
          (name and profile picture) of the person completing the connection; the WhatsApp Business
          Account ID, phone number ID, display phone number, and verified business name for the
          account being connected; and an access token scoped to that WhatsApp Business Account,
          which we store in encrypted form and use only to send and receive messages and manage
          message templates on that business&rsquo;s behalf. We never receive or store a
          user&rsquo;s Facebook password.
        </p>
        <p>
          <strong>Technical and usage data.</strong> Like most web services, we automatically log
          technical information such as IP address, browser and device information, and access
          timestamps, for security, troubleshooting, and reliability purposes.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>2. How We Use Information</h2>
        <ul>
          <li>To provide, operate, and maintain the NeuroEdge Connect platform.</li>
          <li>
            To send and receive WhatsApp messages on behalf of a client business that has explicitly
            connected its own WhatsApp Business Account, and to power automation features (such as
            an automated first reply) that the business itself configures.
          </li>
          <li>To secure the platform, prevent fraud and abuse, and troubleshoot issues.</li>
          <li>To maintain and improve the reliability and functionality of the product.</li>
          <li>To comply with applicable legal obligations.</li>
        </ul>
        <p>
          <strong>
            We do not sell any information, including Platform Data obtained through Facebook or
            Meta APIs, and we do not use it for advertising or any purpose other than providing the
            features described in this policy.
          </strong>
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>3. How We Share Information</h2>
        <ul>
          <li>
            <strong>Within a client&rsquo;s own workspace.</strong> Data is only visible to the
            authorized users of the client business that owns it &mdash; NeuroEdge Connect is
            multi-tenant, and every client&rsquo;s data is isolated from every other client&rsquo;s.
          </li>
          <li>
            <strong>With Meta.</strong> Sending or receiving a WhatsApp message necessarily involves
            Meta&rsquo;s WhatsApp Business Platform; Meta&rsquo;s own privacy policy governs its
            processing of that data.
          </li>
          <li>
            <strong>With service providers.</strong> We use third-party infrastructure providers
            (for example, hosting and database providers) to operate the platform, under
            confidentiality obligations, and only to the extent needed to provide the service.
          </li>
          <li>We do not sell personal information or Platform Data to any third party.</li>
          <li>We may disclose information if required to do so by law or valid legal process.</li>
        </ul>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>4. Data Retention</h2>
        <p>
          We retain information for as long as a client&rsquo;s account is active, plus a reasonable
          period afterward for legal, security, and backup purposes. A client business may request
          deletion of its account and associated data at any time (see Section 7).
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>5. Data Security</h2>
        <p>
          Access tokens obtained via Facebook/Meta Embedded Signup are encrypted at rest
          (AES-256-GCM) and are never returned by our API or displayed in our application.
          Connections to NeuroEdge Connect are encrypted in transit (HTTPS/TLS). Access to the
          platform is controlled by role-based permissions, and every client&rsquo;s data is
          isolated from every other client&rsquo;s at both the application and database level.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>
          6. Facebook / Meta Platform Data &mdash; How We Use It
        </h2>
        <p>This section specifically covers data we obtain through Meta&rsquo;s APIs:</p>
        <ul>
          <li>
            We only request access to a business&rsquo;s WhatsApp Business Account after that
            business explicitly authorizes it through Meta&rsquo;s own Embedded Signup flow.
          </li>
          <li>
            We use this access solely to provide the features the business signed up for: connecting
            their WhatsApp number, sending and receiving messages, and managing message templates on
            their behalf.
          </li>
          <li>
            We do not use Meta Platform Data for advertising, profiling, or any other purpose.
          </li>
          <li>
            Meta Platform Data is retained only for as long as the business keeps its WhatsApp
            channel connected, or as otherwise described in Section 4.
          </li>
          <li>
            A business can revoke NeuroEdge Connect&rsquo;s access at any time, either from its own
            Meta Business Settings or by disconnecting the channel inside NeuroEdge Connect, and may
            request deletion of associated data by contacting us (Section 7).
          </li>
        </ul>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>7. Your Rights and Data Deletion</h2>
        <p>
          Depending on your location, you may have rights to access, correct, delete, or receive a
          copy of your information. To exercise these rights, or to request deletion of your
          account, business data, or any data obtained through Facebook/Meta APIs, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will act on verified deletion
          requests within a reasonable time.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>8. Children&rsquo;s Privacy</h2>
        <p>
          NeuroEdge Connect is a business-to-business product and is not directed at, or knowingly
          used to collect information from, children.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>9. International Data Transfers</h2>
        <p>
          Information may be processed and stored in countries other than your own. Where this
          occurs, we take steps to ensure it continues to receive an appropriate level of
          protection.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>10. Changes to This Policy</h2>
        <p>
          We may update this policy from time to time. Changes will be posted on this page with a
          revised effective date.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: 'var(--font-size-lg)' }}>11. Contact Us</h2>
        <p>
          Neuro Edge Technologies LLP
          <br />
          Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          <br />
          Phone: <a href={contactPhoneHref}>{CONTACT_PHONE_DISPLAY}</a>
        </p>
      </section>

      <p style={{ fontSize: 'var(--font-size-sm)' }}>
        <Link to="/login">&larr; Back to sign in</Link>
      </p>
    </div>
  );
}
